import { useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { PersonButton } from '@/features/people/PersonDrawer';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { BellRing, CheckCircle2, ClipboardCheck, Download, ShieldAlert, ShieldCheck, UserPlus } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ACK_STATUS_LABEL, type AckAudience, type AckRecipientView, type AckStatus, type AckView, type Conversation, type Message, type SendMessageInput } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { messageStore } from '@/lib/messageStore';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Avatar, Button, Field, Input, Label, Modal, Spinner, Toggle, ask } from '@/components/ui';
import { PeoplePicker } from '@/features/people/PeoplePicker';
import { whenWording } from '@/lib/format';
import { useNavigate } from 'react-router-dom';
import { useMobileLayout } from '@/mobile/layout';

export const ackKey = (messageId: string) => ['ack', messageId] as const;
export const ackListKey = (ws: string, box: string) => ['acks', ws, box] as const;
const uk = (iso: string, fmt = "ccc d LLL, HH:mm") => DateTime.fromISO(iso, { zone: 'utc' }).setZone('Europe/London').toFormat(fmt);
const refreshLists = () => void queryClient.invalidateQueries({ queryKey: ['acks'] });

export function useAck(messageId: string, editedAt?: string | null, enabled = true) {
  return useQuery({ queryKey: [...ackKey(messageId), editedAt ?? ''], queryFn: () => api.ack(messageId), enabled, staleTime: 15_000, refetchInterval: 30_000, retry: false });
}

/**
 * t240u: the Due field saves on blur, and closing the window blurs it (the modal focuses it on open). The field only has
 * minute precision while due_at keeps seconds, so comparing the re-built ISO string with due_at always differed and every
 * close PATCHed and said "Saved". Compare in the field's own precision: undefined = unchanged, nothing to save.
 */
export function dueEdit(input: string, dueAt: string | null): string | null | undefined {
  const before = dueAt ? uk(dueAt, "yyyy-LL-dd'T'HH:mm") : '';
  if (input === before) return undefined;
  return input ? DateTime.fromISO(input, { zone: 'Europe/London' }).toUTC().toISO() : null;
}

function Progress({ v }: { v: AckView }) {
  const pct = v.counts.total ? Math.round((v.counts.acknowledged / v.counts.total) * 100) : 0;
  return (
    <div className="flex shrink-0 items-center gap-2">
      <div className="h-1.5 w-28 shrink-0 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
      <span className="whitespace-nowrap text-xs font-medium tabular-nums">{v.counts.acknowledged}/{v.counts.total} acknowledged</span>
    </div>
  );
}

/** Shown under a message that requires acknowledgement. */
export function AckBanner({ m }: { m: Message }) {
  const toast = useUi(s => s.toast);
  const q = useAck(m.id, m.edited_at);
  const [details, setDetails] = useState(false);
  // t239u: on the phone the status is its own page, not this desktop modal.
  const phone = useMobileLayout();
  const nav = useNavigate();
  const openDetails = () => (phone ? nav(`/acknowledgements/${m.id}`) : setDetails(true));
  const ack = useMutation({
    mutationFn: () => api.acknowledge(m.id),
    onSuccess: v => { queryClient.setQueryData([...ackKey(m.id), m.edited_at ?? ''], v); refreshLists(); toast('Acknowledged', 'success'); },
    onError: e => toast(errorMessage(e), 'error'),
  });
  const v = q.data;
  if (!v || v.cancelled_at) return null;
  const due = v.due_at ? ` by ${uk(v.due_at)}` : '';
  const overdue = v.due_at && Date.parse(v.due_at) < Date.now();
  return (
    <div className="mt-1.5 max-w-2xl space-y-1.5" data-testid="ack-banner">
      {v.my_status === 'outstanding' && (
        <div className={clsx('flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2', overdue ? 'border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40' : 'border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/30')}>
          <ShieldAlert className={clsx('h-4 w-4 shrink-0', overdue ? 'text-rose-600' : 'text-orange-700')} />
          <span className={clsx('min-w-0 text-sm', phone ? 'basis-full' : 'flex-1')}>
            {v.my_acknowledged_at
              ? <><b>This message changed</b> after you acknowledged it ({uk(v.my_acknowledged_at)}). Please read it again and acknowledge{due}.</>
              : <><b>Please read and acknowledge</b>{due}.{overdue && <>{' '}<span className="ml-1 font-medium text-rose-700">Overdue</span></>}</>}
          </span>
          <Button size="sm" className={phone ? 'ml-auto' : undefined} loading={ack.isPending} onClick={() => ack.mutate()}><CheckCircle2 className="h-4 w-4" />Acknowledge</Button>
        </div>
      )}
      {v.my_status === 'acknowledged' && v.my_acknowledged_at && (
        <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300"><ShieldCheck className="h-3.5 w-3.5" />You acknowledged this · {uk(v.my_acknowledged_at, 'ccc d LLL yyyy, HH:mm')}</div>
      )}
      {v.my_status === 'changed' && v.my_acknowledged_at && (
        <div className="flex items-center gap-1.5 text-xs text-orange-700 dark:text-orange-300"><ShieldAlert className="h-3.5 w-3.5" />Edited after you acknowledged it ({uk(v.my_acknowledged_at)}) - you acknowledged the earlier version</div>
      )}
      {/* t252u: chat.ack.view_any holders (not in the conversation) get the list too, read-only; recipients is only sent to people who may see it. */}
      {v.can_manage || v.recipients ? (
        <button type="button" onClick={openDetails} className="flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-slate-200 px-3 py-1.5 text-left hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800" data-testid="ack-progress" title="Acknowledgement progress - open details">
          <ClipboardCheck className="h-4 w-4 shrink-0 text-accent-fg" />
          <Progress v={v} />
          {v.due_at && <span aria-hidden className="text-xs text-slate-400">·</span>}
          {v.due_at && <span className={clsx('shrink-0 text-xs', overdue && v.counts.outstanding ? 'font-medium text-rose-600' : 'text-slate-500')}>{whenWording(v.due_at, 'Due', 'Was due', { time: true })}</span>}
          <span aria-hidden className="text-xs text-slate-400">·</span>
          <span className="shrink-0 text-xs font-medium text-accent-fg">Details</span>
        </button>
      ) : v.my_status === null ? (
        <div className="flex items-center gap-1.5 text-xs text-slate-500"><ClipboardCheck className="h-3.5 w-3.5" />Acknowledgement requested from {v.counts.total} {v.counts.total === 1 ? 'person' : 'people'}</div>
      ) : null}
      {details && <AckDetailsModal m={m} onClose={() => setDetails(false)} />}
    </div>
  );
}

const STATUS_CLS: Record<AckStatus, string> = {
  outstanding: 'text-orange-700 dark:text-orange-300', acknowledged: 'text-emerald-700 dark:text-emerald-300', changed: 'text-orange-700', left: 'text-slate-500',
};
const SOURCE_LABEL = { snapshot: 'In channel when posted', joined: 'Joined later', selected: 'Selected', team: 'Via team / group' } as const;

export function AckDetailsModal({ m, onClose }: { m: Message; onClose: () => void }) {
  const toast = useUi(s => s.toast);
  const q = useAck(m.id, m.edited_at);
  const [tab, setTab] = useState<'outstanding' | 'done'>('outstanding');
  const [adding, setAdding] = useState<string[] | null>(null);
  const set = (v: AckView) => { queryClient.setQueryData([...ackKey(m.id), m.edited_at ?? ''], v); refreshLists(); };
  const remind = useMutation({ mutationFn: () => api.remindAck(m.id), onSuccess: r => { toast(`Reminder sent to ${r.sent} ${r.sent === 1 ? 'person' : 'people'}`, 'success'); void q.refetch(); }, onError: e => toast(errorMessage(e), 'error') });
  const upd = useMutation({ mutationFn: (b: Parameters<typeof api.updateAckRequest>[1]) => api.updateAckRequest(m.id, b), onSuccess: v => { set(v); setAdding(null); toast('Saved', 'success'); }, onError: e => toast(errorMessage(e), 'error') });
  const cancel = useMutation({ mutationFn: () => api.cancelAckRequest(m.id), onSuccess: () => { void q.refetch(); refreshLists(); toast('No longer requires acknowledgement', 'success'); onClose(); }, onError: e => toast(errorMessage(e), 'error') });
  const csv = async () => {
    try {
      const text = (await api.ackCsv(m.id)).replace(/^\uFEFF/, '');
      const url = URL.createObjectURL(new Blob(['\uFEFF' + text], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a'); a.href = url; a.download = `acknowledgements-${DateTime.now().toFormat('yyyy-LL-dd')}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const v = q.data;
  const cands = useAckCandidates(m.conversation_id, m.sender_id);
  const addable = useMemo(() => cands.only.filter(id => !(v?.recipients ?? []).some(r => r.user_id === id)), [cands.only, v]);
  const list = (v?.recipients ?? []).filter(r => (tab === 'outstanding' ? r.status === 'outstanding' : r.status !== 'outstanding'));
  return (
    <Modal open wide onClose={onClose} title="Acknowledgements" footer={v && !v.cancelled_at ? (
      <div className="flex w-full flex-wrap items-center gap-2">
        {v.can_manage && <Button variant="ghost" onClick={() => { void ask('Stop requiring acknowledgement? Acknowledgements already given are kept.').then(ok => { if (ok) return cancel.mutate(); }); }}>Stop requiring</Button>}
        <span className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={() => void csv()}><Download className="h-4 w-4" />Export CSV</Button>
          {v.can_manage && <Button disabled={!v.counts.outstanding} loading={remind.isPending} onClick={() => remind.mutate()}><BellRing className="h-4 w-4" />Remind {v.counts.outstanding} outstanding</Button>}
        </span>
      </div>) : undefined}>
      {!v ? <Spinner /> : (
        <div className="space-y-4 text-sm">
          <p className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200">{m.body}</p>
          {v.cancelled_at && <p className="text-xs font-medium text-orange-700">No longer requires acknowledgement (stopped {uk(v.cancelled_at)}). Acknowledgements already given are kept.</p>}
          <div className="space-y-1">
            <Progress v={v} />
            <p className="text-xs text-slate-500">
              {v.audience === 'all' ? `Everyone in the conversation${v.include_new_members ? ', incl. people who join later' : ' when it was posted'}` : `Selected people${v.team_names.length ? ` + ${v.team_names.join(', ')}` : ''}`}
              {' · '}requested by <PersonButton userId={v.created_by} name={v.created_by_name} className="rounded hover:underline">{v.created_by_name}</PersonButton> {uk(v.created_at)}
            </p>
          </div>
          {v.content_changed_at && <p className="text-xs text-orange-700">Edited {uk(v.content_changed_at)} (version {v.content_version}). {v.require_reack ? 'Everyone has to acknowledge the new wording.' : `${v.counts.changed} acknowledged an earlier version.`}</p>}
          {v.last_reminded_at && <p className="text-xs text-slate-500">Last reminder {uk(v.last_reminded_at)}</p>}
          {v.can_manage && <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <Field label="Due (UK time)" hint={v.due_at && v.counts.outstanding > 0 && new Date(v.due_at) < new Date() ? <span className="text-[10px] font-semibold text-rose-700 dark:text-rose-200">Overdue</span> : undefined}>
              <Input aria-label="Due" type="datetime-local" className="w-80" defaultValue={v.due_at ? uk(v.due_at, "yyyy-LL-dd'T'HH:mm") : ''}
                onBlur={e => { const due = dueEdit(e.target.value, v.due_at); if (due !== undefined) upd.mutate({ due_at: due }); }} />
            </Field>
            <Toggle checked={v.require_reack} onChange={c => { if (c !== v.require_reack) upd.mutate({ require_reack: c }); }} label="Edits require acknowledging again" />
            <Button size="sm" variant="ghost" onClick={() => setAdding(adding ? null : [])}><UserPlus className="h-4 w-4" />Add people</Button>
          </div>}
          {adding && (
            <div className="space-y-2">
              <PeoplePicker selected={adding} onChange={setAdding} only={addable} exclude={cands.exclude} />
              <Button size="sm" disabled={!adding.length} loading={upd.isPending} onClick={() => upd.mutate({ add_user_ids: adding })}>Add {adding.length || ''}</Button>
            </div>
          )}
          <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800" role="tablist">
            {([['outstanding', `Not yet (${v.counts.outstanding})`], ['done', `Acknowledged (${v.counts.acknowledged})${v.counts.left ? ` · left (${v.counts.left})` : ''}`]] as const).map(([id, label]) => (
              <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} className={clsx('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === id ? 'border-accent-line text-accent-fg' : 'border-transparent text-slate-500')}>{label}</button>
            ))}
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {list.length === 0 && <li className="py-4 text-center text-slate-500">{tab === 'outstanding' ? 'Everyone has acknowledged 🎉' : 'Nobody yet'}</li>}
            {list.map((r: AckRecipientView) => (
              <li key={r.user_id} className="flex items-center gap-3 py-2">
                <Avatar size={28} name={r.display_name} id={r.user_id} />
                <div className="min-w-0 flex-1"><div className="font-medium"><PersonButton userId={r.user_id} name={r.display_name} className="rounded hover:underline">{r.display_name}</PersonButton></div><div className="text-xs text-slate-500">{SOURCE_LABEL[r.source]}</div></div>
                <div className={clsx('text-right text-xs', STATUS_CLS[r.status])}>
                  {ACK_STATUS_LABEL[r.status]}
                  {r.acknowledged_at && <div className="text-slate-500">{r.acknowledged_version && r.acknowledged_version < v.content_version ? `Acknowledged v${r.acknowledged_version} · ` : ''}{uk(r.acknowledged_at, 'ccc d LLL yyyy, HH:mm:ss')}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

export interface AckDraft { conversationId: string; channelLabel: string; memberCount: number; message?: Message; send?: Omit<SendMessageInput, 'client_message_id'> & { attachments?: Message['attachments'] }; onDone?: () => void }

/** t149: who the ack pickers may offer: the conversation's members, never the message's author. */
function useAckCandidates(conversationId: string, authorId: string | null | undefined) {
  const conv = useQuery({ queryKey: qk.conversation(conversationId), queryFn: () => api.conversation(conversationId), staleTime: 30_000 });
  const only = useMemo(() => conv.data?.members?.map(m => m.user_id), [conv.data]);
  const exclude = useMemo(() => (authorId ? [authorId] : []), [authorId]);
  return { only: only ?? [], exclude };
}

/** Choose who must acknowledge. Used from the composer (post + require) and from a message's menu (afterwards). */
export function AckRequestModal({ draft, onClose }: { draft: AckDraft; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const teams = useQuery({ queryKey: ['teams', ws], queryFn: () => api.teams(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 }).data?.teams ?? [];
  const [audience, setAudience] = useState<AckAudience>('all');
  const { userId } = useAuth();
  const cands = useAckCandidates(draft.conversationId, draft.message?.sender_id ?? userId);
  const [people, setPeople] = useState<string[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [joiners, setJoiners] = useState(true);
  // Channel settings → Posting can pre-fill a due time (hours after posting).
  const dueHours = queryClient.getQueryData<Conversation>(qk.conversation(draft.conversationId))?.settings?.ack_due_hours;
  const [due, setDue] = useState(() => dueHours ? DateTime.now().setZone('Europe/London').plus({ hours: dueHours }).toFormat("yyyy-LL-dd'T'HH:mm") : '');
  const [reack, setReack] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    if (audience === 'selected' && !people.length && !teamIds.length) { setErr('Pick at least one person or team'); return; }
    setBusy(true);
    try {
      let msg = draft.message ?? null;
      if (!msg && draft.send) { msg = await messageStore.send(draft.conversationId, draft.send); if (!msg) throw new Error('The message could not be sent'); draft.onDone?.(); }
      const dueIso = due ? DateTime.fromISO(due, { zone: 'Europe/London' }).toUTC().toISO() : null;
      const v = await api.requestAck(msg!.id, { audience, user_ids: audience === 'selected' ? people : undefined, team_ids: audience === 'selected' ? teamIds : undefined, include_new_members: joiners, due_at: dueIso, require_reack: reack });
      queryClient.setQueryData([...ackKey(msg!.id), msg!.edited_at ?? ''], v);
      refreshLists();
      toast(`Acknowledgement requested from ${v.counts.total} ${v.counts.total === 1 ? 'person' : 'people'}`, 'success');
      onClose();
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={draft.message ? 'Require acknowledgement' : 'Post and require acknowledgement'} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button loading={busy} onClick={() => void submit()}>{draft.message ? 'Require acknowledgement' : 'Post'}</Button>
    </>}>
      <div className="space-y-4 text-sm">
        <p className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 dark:bg-slate-800/60">{draft.message?.body ?? draft.send?.body}</p>
        <div role="radiogroup" aria-label="Who must acknowledge" className="space-y-2">
          <Label>Who must acknowledge</Label>
          <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={audience === 'all'} onChange={() => setAudience('all')} />
            <span>Everyone in {draft.channelLabel} <span className="text-slate-500">({Math.max(0, draft.memberCount - 1)} {draft.memberCount - 1 === 1 ? 'person' : 'people'} besides the author)</span></span></label>
          {audience === 'all' && <div className="ml-6"><Toggle checked={joiners} onChange={setJoiners} label="Also people who join later" /></div>}
          <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={audience === 'selected'} onChange={() => setAudience('selected')} /><span>Selected people, teams or groups</span></label>
          {audience === 'selected' && (
            <div className="ml-6 space-y-2">
              <PeoplePicker selected={people} onChange={setPeople} only={cands.only} exclude={cands.exclude} />
              <div className="flex flex-wrap gap-1.5">
                {teams.map(t => (
                  <button key={t.id} type="button" aria-pressed={teamIds.includes(t.id)} onClick={() => setTeamIds(teamIds.includes(t.id) ? teamIds.filter(x => x !== t.id) : [...teamIds, t.id])}
                    className={clsx('rounded-full border px-2.5 py-0.5 text-xs', teamIds.includes(t.id) ? 'border-accent-line bg-accent-soft text-accent-fg' : 'border-slate-200 text-slate-600 hover:border-slate-300')}>{t.name}</button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Departments and groups include their members who are in this conversation.</p>
            </div>
          )}
        </div>
        <Field label="Due (optional, UK time)"><Input aria-label="Due date" type="datetime-local" className="w-80 max-w-full" value={due} onChange={e => setDue(e.target.value)} /></Field>
        <Toggle checked={reack} onChange={setReack} label="If the message is edited, everyone must acknowledge again" />
        <p className="text-xs text-slate-500">People are notified now; you'll see who has acknowledged and can send reminders. With a due date they're reminded automatically the day before and when it's overdue.</p>
        {err && <p className="text-sm text-rose-600">{err}</p>}
      </div>
    </Modal>
  );
}
